import { createKeybindingsHandler, type KeybindingHandlerOptions } from "tinykeys";
import type { CommandId, CommandSpec } from "./keymap";

export interface DispatchDeps {
  /** Runs a command; returns false when nothing handles it on the current screen. */
  run: (id: CommandId) => boolean;
  /** Events that must never trigger shortcuts (typing, dialogs). */
  ignore: (event: KeyboardEvent) => boolean;
  /** Per-binding veto (e.g. Enter on a focused button). */
  skip?: (event: KeyboardEvent, spec: CommandSpec) => boolean;
  timeout?: number;
}

/**
 * One keydown handler for all shortcuts.
 *
 * tinykeys stops after the first binding that fires and leaves later multi-key bindings mid-
 * sequence, so a fast "g a" followed by "g s" would never open Settings. Each sequence therefore
 * gets its own tinykeys handler (its state cannot go stale), single keys share one handler that
 * runs last, and the first binding whose command runs marks the event so "s" does not also fire
 * after "g s".
 *
 * Several commands may share a binding (Enter confirms an armed action, else opens the card):
 * they are tried in the order given and the first one with a handler on this screen runs.
 * Commands marked `noRepeat` ignore auto-repeated key events.
 */
export function createShortcutHandler(
  bindings: readonly (readonly [string, CommandSpec])[],
  deps: DispatchDeps,
): (event: KeyboardEvent) => void {
  const handled = new WeakSet<KeyboardEvent>();
  const options: KeybindingHandlerOptions = { ignore: deps.ignore, timeout: deps.timeout ?? 1000 };
  const callbackFor = (specs: readonly CommandSpec[]) => (event: KeyboardEvent) => {
    if (handled.has(event)) return;
    for (const spec of specs) {
      if (spec.noRepeat && event.repeat) continue;
      if (deps.skip?.(event, spec)) continue;
      if (deps.run(spec.id)) {
        event.preventDefault();
        handled.add(event);
        return;
      }
    }
  };
  const grouped = new Map<string, CommandSpec[]>();
  for (const [binding, spec] of bindings) {
    const list = grouped.get(binding) ?? [];
    list.push(spec);
    grouped.set(binding, list);
  }
  const isSequence = (binding: string) => binding.trim().split(" ").length > 1;
  const sequenceHandlers = [...grouped]
    .filter(([binding]) => isSequence(binding))
    .map(([binding, specs]) =>
      createKeybindingsHandler({ [binding]: callbackFor(specs) }, options),
    );
  const singles: Record<string, (event: KeyboardEvent) => void> = {};
  for (const [binding, specs] of grouped) {
    if (!isSequence(binding)) singles[binding] = callbackFor(specs);
  }
  const singleHandler = createKeybindingsHandler(singles, options);
  return (event) => {
    for (const handler of sequenceHandlers) handler(event);
    singleHandler(event);
  };
}
